/**
 * EventItem Component
 * Individual event card with expand/collapse functionality
 * Design based on Figma specifications
 */

import { useState } from "react";
import { Avatar, Tooltip } from "@nextui-org/react";
import { ChevronDown } from "lucide-react";
import VendorLogo from "@/shared/components/VendorSearchAutocomplete/VendorLogo";
import { ZButton } from "@/shared/components/button";
import { cn } from "@/shared/utils/classname-merger";
import type { RecentEvent } from "../types";

interface EventItemProps {
  event: RecentEvent;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * Source Badge component following existing pattern from risksColumns.tsx
 * Shows a numbered badge that opens a tooltip with URL info
 */
function SourceBadge({ url }: { url: string }) {
  const [isOpen, setIsOpen] = useState(false);

  let domain = "";
  try {
    domain = new URL(url).hostname;
  } catch {
    domain = url;
  }

  const handleViewClick = () => {
    setIsOpen(false);
    window.open(url, "_blank");
  };

  return (
    <div className="flex items-center gap-1 py-0.5">
      <Tooltip
        showArrow
        placement="top"
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        classNames={{
          content: "bg-black/95 backdrop-blur-md text-white text-tiny p-3",
          arrow: "backdrop-blur-md",
          base: "before:bg-black/95",
        }}
        content={
          <div className="flex flex-col gap-2 max-w-96">
            <div className="max-h-20 overflow-hidden">
              <p className="line-clamp-4 text-white break-all">{url}</p>
            </div>
            <p className="text-tiny text-[#A5A5A5] break-all">{domain}</p>
            <div className="self-end">
              <ZButton size="sm" type="button" onClick={handleViewClick}>
                View Reference
              </ZButton>
            </div>
          </div>
        }
      >
        <Avatar
          classNames={{
            base: cn(
              "flex relative justify-center items-center box-border overflow-hidden",
              "w-4 h-4 bg-[#006FEE] bg-opacity-20 rounded-full cursor-pointer",
              "border border-white"
            ),
          }}
          icon={
            <span className="text-[10px] font-medium leading-4 text-center text-[#006FEE]">
              1
            </span>
          }
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        />
      </Tooltip>
    </div>
  );
}

export function EventItem({ event, isExpanded, onToggle }: EventItemProps) {
  const firstLetter = event.vendor_name?.trim().charAt(0).toUpperCase() || "?";

  return (
    <div
      className={cn(
        "flex flex-col p-4 gap-3 bg-[#F8FAFC] rounded-lg",
        "transition-all duration-200"
      )}
    >
      {/* Header Row */}
      <div className="flex items-center gap-3">
        {/* Vendor Logo - Reusing existing component */}
        {event.vendor_logo_url ? (
          <VendorLogo
            src={event.vendor_logo_url}
            alt={event.vendor_name}
            size={20}
            needsAuthToken={true}
          />
        ) : (
          <div
            className="flex items-center justify-center"
            style={{
              width: "20px",
              height: "20px",
              background: "#FAFAFA",
              border: "1px solid #E2E8F0",
              borderRadius: "24px",
            }}
          >
            <span
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "12px",
                lineHeight: "16px",
                textAlign: "center",
                color: "#71717A",
              }}
            >
              {firstLetter}
            </span>
          </div>
        )}

        {/* Vendor and Product Name */}
        <span className="text-sm font-medium leading-5 text-[#0F172B] flex-grow truncate">
          {[event.vendor_name, event.product_name].filter(Boolean).join(' ')}
        </span>

        {/* Chevron Toggle */}
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center justify-center w-5 h-5 flex-shrink-0 hover:opacity-70 transition-opacity"
          aria-label={isExpanded ? "Collapse event" : "Expand event"}
        >
          <ChevronDown
            className={cn(
              "w-5 h-5 text-[#006FEE] transition-transform duration-200",
              isExpanded && "rotate-180"
            )}
            strokeWidth={1.5}
          />
        </button>
      </div>

      {/* Event Title */}
      <p className="text-sm font-medium leading-5 text-[#314158]">
        {event.title}
      </p>

      {/* Expanded Content */}
      {isExpanded && (
        <>
          {event.description && (
            <p className="text-sm font-normal leading-5 text-[#314158]">
              {event.description}
            </p>
          )}
          {/* Source Badge */}
          {event.source_url && <SourceBadge url={event.source_url} />}
        </>
      )}
    </div>
  );
}

export default EventItem;
